import { Star, Truck, RotateCcw, ShieldCheck, Award, BadgeCheck } from "lucide-react";
import useReveal from "@/hooks/useReveal";

const testimonials = [
  {
    name: "Maya Okonkwo",
    verified: true,
    rating: 5,
    quote:
      "The Meridian X shaved 40 seconds off my 10K PR. The energy return is unreal — I actually feel faster than I am.",
    avatar: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&w=200&q=85",
  },
  {
    name: "Devin Park",
    verified: true,
    rating: 5,
    quote:
      "I'm on my feet 12 hours a day. STRYDE is the first pair that hasn't destroyed my heels by week three.",
    avatar: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=200&q=85",
  },
  {
    name: "Lena Fischer",
    verified: true,
    rating: 5,
    quote:
      "Sustainable, gorgeous, and they actually last. Bought my third pair last week. My closet has a problem.",
    avatar: "https://images.unsplash.com/photo-1531123897727-8f129e1688ce?auto=format&fit=crop&w=200&q=85",
  },
];

const trustBadges = [
  { icon: Truck, label: "Free shipping" },
  { icon: RotateCcw, label: "30-day returns" },
  { icon: ShieldCheck, label: "Secure checkout" },
  { icon: Award, label: "2-year warranty" },
];

const press = ["RUNNER'S WORLD", "GQ", "HYPEBEAST", "WIRED", "COMPLEX", "OUTSIDE"];

export default function Testimonials() {
  const ref = useReveal();
  return (
    <section
      ref={ref}
      data-testid="testimonials-section"
      className="reveal bg-white py-24 md:py-32"
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <span className="mono text-xs uppercase tracking-widest text-[#ff5722]">The verdict</span>
            <h2 className="font-display font-extrabold text-4xl md:text-5xl lg:text-6xl leading-[0.95] mt-3 tracking-tight">
              4,208 reviews.<br />
              <span className="italic font-light">One consensus.</span>
            </h2>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-0.5">
              {[0,1,2,3,4].map(i => <Star key={i} size={22} className="star-active" />)}
            </div>
            <span className="font-display font-bold text-2xl">4.8</span>
            <span className="text-black/60 text-sm">/ 5 average</span>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-5 md:gap-6">
          {testimonials.map((t, i) => (
            <figure
              key={t.name}
              data-testid={`testimonial-${i}`}
              className="p-7 rounded-2xl border border-black/10 hover:border-black/30 transition-colors bg-[#faf9f5]"
            >
              <div className="flex items-center gap-0.5 mb-4">
                {[0,1,2,3,4].map(k => (
                  <Star key={k} size={14} className={k < t.rating ? "star-active" : "star-inactive"} />
                ))}
              </div>
              <blockquote className="text-base md:text-lg leading-relaxed text-black/80">
                &ldquo;{t.quote}&rdquo;
              </blockquote>
              <figcaption className="mt-6 flex items-center gap-3">
                <img
                  src={t.avatar}
                  alt=""
                  aria-hidden="true"
                  className="w-10 h-10 rounded-full object-cover"
                  loading="lazy"
                />
                <div>
                  <div className="font-semibold text-sm flex items-center gap-1.5">
                    {t.name}
                    {t.verified && <BadgeCheck size={14} className="text-[#ff5722]" />}
                  </div>
                  <div className="text-xs text-black/50 mono">Verified buyer</div>
                </div>
              </figcaption>
            </figure>
          ))}
        </div>

        {/* Trust badges */}
        <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5">
          {trustBadges.map((b) => {
            const Icon = b.icon;
            return (
              <div
                key={b.label}
                className="flex items-center gap-3 p-5 rounded-xl border border-black/10 bg-[#faf9f5]"
                data-testid={`trust-${b.label.replace(/\s+/g, "-").toLowerCase()}`}
              >
                <Icon size={22} strokeWidth={1.7} />
                <span className="text-sm font-semibold">{b.label}</span>
              </div>
            );
          })}
        </div>

        {/* Press */}
        <div className="mt-16 text-center">
          <span className="mono text-xs uppercase tracking-widest text-black/40">As seen in</span>
          <div className="mt-5 flex flex-wrap items-center justify-center gap-x-10 gap-y-4">
            {press.map((p) => (
              <span key={p} className="font-display font-bold text-lg md:text-xl text-black/40 hover:text-black/80 transition-colors tracking-wide">
                {p}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
